import React, { useState, memo } from "react";
import { AHSeparator, Box, ListItem, EmployeeAvatar, Dialog } from "arkh";

import { graphql, useFragment } from "react-relay";

import { nameMapper, useFilterSearch } from "@/src/components/mobile/useSearch";

import { SearchInput } from "@/src/features/Products/SearchInput";

import { TodoAssigneesPopover_EmployeeConnection$key } from "./__generated__/TodoAssigneesPopover_EmployeeConnection.graphql";
import { TodoAssigneesPopover_EmployeeItem$key } from "./__generated__/TodoAssigneesPopover_EmployeeItem.graphql";
import { useEvent } from "widgets/src/useEvent";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import { FlashList } from "@shopify/flash-list";
import { KeyboardAwareFlashListScrollComponent } from "@/src/components/mobile/KeyboardAwareFlashListScrollComponent";
import { onPressInHaptic } from "widgets/src/onPressInHaptic";
import { TodoAssigneesPopover_Roles$key } from "./__generated__/TodoAssigneesPopover_Roles.graphql";
import { RoleListItem } from "widgets/src/RoleListItem/RoleListItem";

type TodoAssigneesPopoverProps = {
  assignEmployeeIds: string[];
  setAssignEmployeeIds: (assignEmployeeIds: string[]) => void;
  assignRoleIds: string[];
  setAssignRoleIds: (assignRoleIds: string[]) => void;
  employeeConnection: TodoAssigneesPopover_EmployeeConnection$key;
  roles: TodoAssigneesPopover_Roles$key;
  disabled?: boolean;
  rolesLocked?: boolean;
  onClose: () => void;
  visible: boolean;
};

export function TodoAssigneesPopover(props: TodoAssigneesPopoverProps) {
  const [query, setQuery] = useState("");
  const [initialEmployeeIds] = useState<string[]>(props.assignEmployeeIds); // we set the key to reset this
  const [initialRoleIds] = useState<string[]>(props.assignRoleIds); // we set the key to reset this
  const employeeConnection = useFragment(
    graphql`
      fragment TodoAssigneesPopover_EmployeeConnection on EmployeeConnection {
        edges {
          node {
            id
            name
            ...TodoAssigneesPopover_EmployeeItem
          }
        }
      }
    `,
    props.employeeConnection
  );

  const roles = useFragment(
    graphql`
      fragment TodoAssigneesPopover_Roles on EmployeeRole @relay(plural: true) {
        id
        name
        ...RoleListItem_role
      }
    `,
    props.roles
  );

  const filteredRoles = (() => {
    const validRoles = roles?.filter((r) => r) ?? [];
    if (!query) return validRoles;
    const lowerQuery = query.toLowerCase();
    return validRoles.filter((r) => r.name?.toLowerCase().includes(lowerQuery));
  })();

  const employees =
    employeeConnection.edges
      .map((edge) => edge.node)
      .filter((node) => !!node) ?? [];

  const [, filteredEmployees] = useFilterSearch(query, employees, nameMapper);

  const {
    assignEmployeeIds,
    setAssignEmployeeIds,
    assignRoleIds,
    setAssignRoleIds,
  } = props;

  type RoleEntry = { type: "role"; role: (typeof filteredRoles)[number] };
  type EmployeeEntry = {
    type: "employee";
    employee: (typeof filteredEmployees)[number];
    index: number;
  };
  type ListItemType = RoleEntry | EmployeeEntry;

  // Sort order: selected roles, selected employees, non-selected roles, non-selected employees
  // Employee order is preserved from backend (already sorted by work status)
  const sortedData = (() => {
    const roleItems: RoleEntry[] = filteredRoles.map((role) => ({
      type: "role" as const,
      role,
    }));
    const employeeItems: EmployeeEntry[] = filteredEmployees.map(
      (employee, index) => ({
        type: "employee" as const,
        employee,
        index,
      })
    );

    const allItems = [...roleItems, ...employeeItems];

    return allItems.sort((a, b) => {
      const aId = a.type === "role" ? a.role.id : a.employee.id;
      const bId = b.type === "role" ? b.role.id : b.employee.id;

      const aSelected = (a.type === "role"
        ? initialRoleIds
        : initialEmployeeIds
      ).includes(aId)
        ? 1
        : 0;
      const bSelected = (b.type === "role"
        ? initialRoleIds
        : initialEmployeeIds
      ).includes(bId)
        ? 1
        : 0;

      // First sort by selected state
      if (bSelected !== aSelected) return bSelected - aSelected;

      // Then sort roles before employees
      const aTypeOrder = a.type === "role" ? 1 : 0;
      const bTypeOrder = b.type === "role" ? 1 : 0;
      if (bTypeOrder !== aTypeOrder) return bTypeOrder - aTypeOrder;

      // For roles, sort alphabetically
      if (a.type === "role" && b.type === "role") {
        const aName = a.role.name ?? "";
        const bName = b.role.name ?? "";
        return aName.localeCompare(bName);
      }

      // For employees, preserve backend order
      if (a.type === "employee" && b.type === "employee") {
        return a.index - b.index;
      }

      return 0;
    });
  })();

  const onPress = useEvent((id: string) => {
    onPressInHaptic();

    setAssignEmployeeIds(
      assignEmployeeIds.includes(id)
        ? assignEmployeeIds.filter((existingId) => existingId !== id)
        : [...assignEmployeeIds, id]
    );
  });

  const onPressRole = useEvent((id: string) => {
    onPressInHaptic();

    setAssignRoleIds(
      assignRoleIds.includes(id)
        ? assignRoleIds.filter((existingId) => existingId !== id)
        : [...assignRoleIds, id]
    );
  });

  const renderItem = ({ item }: { item: ListItemType }) => {
    if (item.type === "role") {
      const selected = assignRoleIds.includes(item.role.id);
      return (
        <RoleListItem
          disabled={props.disabled ?? false}
          locked={props.rolesLocked}
          role={item.role}
          selected={selected}
          onPress={onPressRole}
        />
      );
    }

    const selected = assignEmployeeIds.includes(item.employee.id);
    return (
      <EmployeeItem
        disabled={props.disabled ?? false}
        employee={item.employee}
        selected={selected}
        onPress={onPress}
      />
    );
  };

  const intl_tokens = useIntlTokens();
  return (
    <Dialog
      visible={props.visible}
      onClose={props.onClose}
      height={"80%"}
      maxHeight={600}
      title={intl_tokens.Assignees}
    >
      <AHSeparator />
      <Box style={{ flex: 1 }}>
        <Box color="surface">
          <SearchInput
            isSearching
            query={query}
            onChange={(e) => setQuery(e.nativeEvent.text)}
            returnKeyType="done"
            autoCapitalize="words"
          />
        </Box>

        <FlashList
          data={sortedData}
          renderItem={renderItem}
          keyExtractor={(item) =>
            item.type === "role"
              ? `role-${item.role.id}`
              : `emp-${item.employee.id}`
          }
          ItemSeparatorComponent={AHSeparator}
          renderScrollComponent={KeyboardAwareFlashListScrollComponent}
        />
      </Box>
    </Dialog>
  );
}

const EmployeeItem = memo(EmployeeItemC);
function EmployeeItemC(props: {
  employee: TodoAssigneesPopover_EmployeeItem$key;
  selected: boolean;
  onPress: (employeeId: string) => void;
  disabled: boolean;
}) {
  const employee = useFragment(
    graphql`
      fragment TodoAssigneesPopover_EmployeeItem on Employee {
        id
        name

        ...Avatar_employee
      }
    `,
    props.employee
  );
  return (
    <ListItem
      disabled={props.disabled}
      onPress={() => props.onPress(employee.id)}
      left={<EmployeeAvatar employee={employee} size="large" />}
      right={<ListItem.SelectIndicator selected={props.selected} />}
    >
      <ListItem.Title>{employee.name}</ListItem.Title>
    </ListItem>
  );
}
